import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, DocumentTextIcon, CalendarIcon, LightBulbIcon, ChartBarIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import SEO from '../SEO';

export default function AuditThankYou() {
  const [email, setEmail] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [insights, setInsights] = useState([]);
  
  // Try to get email and audit data from sessionStorage
  useEffect(() => {
    // Ensure we scroll to the top of the page
    window.scrollTo(0, 0);
    
    try {
      const auditDataStr = sessionStorage.getItem('auditData');
      if (auditDataStr) {
        const parsedData = JSON.parse(auditDataStr);
        setAuditData(parsedData);
        
        if (parsedData.email) {
          setEmail(parsedData.email);
        }
        
        // Process insights if available
        if (parsedData.aiInsights) {
          processInsights(parsedData.aiInsights);
        }
      }
    } catch (err) {
      console.error('Error retrieving audit data:', err);
    }
  }, []);
  
  // Process the AI insights string into formatted sections
  const processInsights = (aiInsightsStr) => {
    try {
      const processedInsights = [];
      
      // Helper function to extract sections more consistently
      const extractSection = (sectionName, text) => {
        const regex = new RegExp(`${sectionName}:\\s*(.*?)(?=(AI OPPORTUNITIES:|QUICK WINS:|ROADMAP:|$))`, 'is');
        const match = text.match(regex);
        return match ? match[1].trim() : '';
      };
      
      // Helper function to extract numbered items from a section
      const extractNumberedItems = (sectionText) => {
        // This regex looks for numbered items (1., 2., etc.) and captures everything until the next numbered item or end
        const itemRegex = /(\d+\.\s*)(.*?)(?=(?:\n\s*\d+\.)|$)/gs;
        const items = [];
        let match;
        
        while ((match = itemRegex.exec(sectionText)) !== null) {
          items.push(match[2].trim());
        }
        
        return items;
      };
      
      // Extract sections
      const opportunitiesText = extractSection('AI OPPORTUNITIES', aiInsightsStr);
      const quickWinsText = extractSection('QUICK WINS', aiInsightsStr);
      const roadmapText = extractSection('ROADMAP', aiInsightsStr);
      
      // If sections were found, add them to processed insights
      if (opportunitiesText) {
        processedInsights.push({
          title: "AI Opportunities",
          content: opportunitiesText,
          icon: LightBulbIcon,
          items: extractNumberedItems(opportunitiesText)
        });
      }
      
      if (quickWinsText) {
        processedInsights.push({
          title: "Quick Wins",
          content: quickWinsText,
          icon: RocketLaunchIcon,
          items: extractNumberedItems(quickWinsText)
        });
      }
      
      if (roadmapText) {
        processedInsights.push({
          title: "Implementation Roadmap",
          content: roadmapText,
          icon: ChartBarIcon
        });
      }
      
      // If no structured sections were found, use the full response
      if (processedInsights.length === 0) {
        processedInsights.push({
          title: "AI Analysis Results",
          content: aiInsightsStr,
          icon: DocumentTextIcon
        });
      }
      
      // Generate fallback insights if we don't have enough opportunities
      if (processedInsights.length === 0 || 
          (processedInsights[0]?.title === "AI Opportunities" && 
           processedInsights[0]?.items?.length < 2)) {
        
        // Add industry-specific fallback if needed
        const fallbackInsight = generateIndustrySpecificInsight(
          auditData?.industry || 'other',
          auditData?.businessName || 'your business'
        );
        
        processedInsights.unshift({
          title: "AI Opportunities",
          content: `1. Title: ${fallbackInsight.title}\nDescription: ${fallbackInsight.description}\nBusiness Impact: ${fallbackInsight.impact}\nImplementation Complexity: Medium`,
          icon: LightBulbIcon,
          fallback: true
        });
      }
      
      setInsights(processedInsights);
    } catch (err) {
      console.error('Error processing insights:', err);
      // Generate fallback insights in case of parsing error
      const fallbackInsights = generateFallbackInsights(auditData);
      setInsights([
        {
          title: "AI Opportunities",
          content: fallbackInsights.map((insight, idx) => 
            `${idx + 1}. Title: ${insight.title}\nDescription: ${insight.description}\nBusiness Impact: ${insight.impact || 'Improved business outcomes and efficiency.'}\nImplementation Complexity: ${insight.complexity || 'Medium'}`
          ).join('\n\n'),
          icon: LightBulbIcon,
          fallback: true
        }
      ]);
    }
  };
  
  // Helper function to generate industry-specific fallback insights
  const generateIndustrySpecificInsight = (industry, businessName) => {
    switch(industry) {
      case 'retail':
        return {
          title: "Customer Experience Personalisation",
          description: `Retail businesses like ${businessName} can achieve up to 30% increase in customer satisfaction and 25% higher average order values with AI-driven personalisation systems.`,
          impact: "Increased customer loyalty and higher average order values."
        };
      case 'finance':
        return {
          title: "AI-Powered Risk Assessment",
          description: `Financial institutions like ${businessName} using AI for risk assessment typically report 35% fewer false positives in fraud detection while improving regulatory compliance.`,
          impact: "Reduced fraud losses and improved regulatory compliance."
        };
      case 'healthcare':
        return {
          title: "Patient Care Optimisation",
          description: `Healthcare providers implementing AI-powered scheduling and resource allocation systems report a 40% decrease in administrative overhead and significantly improved patient satisfaction.`,
          impact: "Reduced administrative costs and improved patient outcomes."
        };
      case 'manufacturing':
        return {
          title: "Predictive Maintenance Solutions",
          description: `Manufacturing operations like ${businessName} can reduce equipment downtime by up to 45% and maintenance costs by 30% through AI-powered predictive maintenance systems.`,
          impact: "Reduced downtime and lower maintenance costs."
        };
      default:
        return {
          title: "Process Automation Opportunity",
          description: `Businesses in your sector typically reduce operational costs by 30% and improve productivity by 25% through strategic AI implementation in core workflows.`,
          impact: "Significant cost savings and productivity improvements."
        };
    }
  };
  
  // Function to generate fallback insights if AI parsing fails
  const generateFallbackInsights = (data) => {
    const insights = [];
    if (!data) return insights;
    
    const { industry, currentAIUse, businessGoals, businessName } = data;
    
    // Add industry-specific insight
    const industryInsight = generateIndustrySpecificInsight(industry, businessName);
    insights.push({
      ...industryInsight,
      complexity: "Medium"
    });
    
    // Based on current AI usage
    if (currentAIUse === 'none') {
      insights.push({
        title: "AI Implementation Roadmap",
        description: "A detailed step-by-step plan for introducing AI capabilities into your existing operations with minimal disruption and maximum impact.",
        impact: "Smooth transition to AI-enhanced operations with minimal business disruption.",
        complexity: "Medium"
      });
    } else if (currentAIUse === 'basic') {
      insights.push({
        title: "AI Integration Strategy",
        description: "Connect your existing AI tools into a cohesive ecosystem for maximum ROI and operational efficiency.",
        impact: "Increased ROI from existing AI investments and improved data flow.",
        complexity: "Medium"
      });
    } else {
      insights.push({
        title: "AI Capability Expansion",
        description: "Advanced implementation strategies to build upon your existing AI foundation and drive further business value.",
        impact: "Enhanced capabilities and competitive advantage through advanced AI.",
        complexity: "High"
      });
    }
    
    // Add a goal-specific insight if we have business goals
    if (businessGoals && businessGoals.includes('efficiency')) {
      insights.push({
        title: "Operational Efficiency Analysis",
        description: "Specific areas where AI can streamline your business processes based on industry benchmarks and your current workflows.",
        impact: "25-35% reduction in manual processes and administrative overhead.",
        complexity: "Low"
      });
    } else if (businessGoals && businessGoals.includes('cx')) {
      insights.push({
        title: "Customer Experience Enhancement",
        description: "AI tools specifically designed to improve customer satisfaction and loyalty in your industry, with implementation guidance.",
        impact: "Increased customer satisfaction scores by 30% and improved retention rates.",
        complexity: "Medium"
      });
    }
    
    return insights;
  };
  
  return (
    <>
      <SEO 
        title="Thank You for Your Purchase | BetterByAI"
        description="Thank you for purchasing your full AI business audit report."
        canonical="https://betterbyai.com/audit-results/thank-you"
      />
    
      <section className="pt-32 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center justify-center p-4 bg-green-100 rounded-full mb-6">
                <CheckCircleIcon className="w-12 h-12 text-green-600" />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Thank You for Your Purchase!
              </h1>
              
              <p className="text-xl text-secondary mb-4">
                Your payment has been successfully processed.
              </p>
            </motion.div>
            
            {/* Enhanced Email Notification Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100"
            >
              <div className="mb-6 text-center border-b pb-6">
                <div className="bg-[#FF7066]/10 inline-flex p-3 rounded-lg mb-4">
                  <DocumentTextIcon className="w-8 h-8 text-[#FF7066]" />
                </div>
                <h2 className="text-2xl font-bold text-primary mb-3">Your Premium AI Audit Report is on the Way</h2>
                <div className="w-16 h-1 bg-gradient-to-r from-[#FF7066] to-secondary mx-auto mb-4"></div>
              </div>
              
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF7066]/10 flex items-center justify-center">
                  <span className="text-[#FF7066] font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-medium text-primary mb-1">Initial AI Analysis</h3>
                  <p className="text-secondary text-sm">
                    You can view your initial AI-generated insights below on this page
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF7066]/10 flex items-center justify-center">
                  <span className="text-[#FF7066] font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-medium text-primary mb-1">Expert Review & Enhancement</h3>
                  <p className="text-secondary text-sm">
                    Our AI consultants are reviewing your data to provide additional strategic insights
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FF7066]/10 flex items-center justify-center">
                  <span className="text-[#FF7066] font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-medium text-primary mb-1">Comprehensive PDF Delivery</h3>
                  <p className="text-secondary text-sm">
                    Your detailed report will be emailed to {email ? <span className="font-medium">{email}</span> : "the email address you provided"} within 48-72 hours depending on complexity
                  </p>
                </div>
              </div>
              
              <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <div className="flex items-center text-sm">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                  <p>
                    <span className="font-medium">Haven't received your report?</span> Check your spam folder or contact <a href="mailto:support@betterbyai.com" className="text-[#FF7066] hover:underline">support@betterbyai.com</a>
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Full AI Audit Results Section */}
            {insights.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100"
              >
                <h2 className="text-2xl font-bold text-primary mb-6 text-center">Your AI Audit Results</h2>
                <p className="text-center text-secondary mb-8">
                  Here is your initial AI analysis for {auditData?.businessName || "your business"}
                </p>
                
                {insights.map((insight, index) => {
                  // Custom parse the content to create a better structure
                  const opportunities = [];
                  const rawContent = insight.content;
                  
                  // Extract individual numbered items (1., 2., 3., etc.)
                  const itemRegex = /(\d+\.)\s*([\s\S]*?)(?=\d+\.|$)/g;
                  let match;
                  
                  while ((match = itemRegex.exec(rawContent)) !== null) {
                    const itemContent = match[2].trim();
                    
                    // Extract structured information from each item
                    const titleMatch = itemContent.match(/Title:\s*(.*?)(?=\n|Description:|$)/i);
                    const descMatch = itemContent.match(/Description:\s*(.*?)(?=\n|Business Impact:|$)/i);
                    const impactMatch = itemContent.match(/Business Impact(?:\/Benefit)?:\s*(.*?)(?=\n|Implementation Complexity:|$)/i);
                    const complexityMatch = itemContent.match(/Implementation Complexity:\s*(.*?)(?=\n|$)/i);
                    
                    // Add structured item if we found meaningful content
                    if (titleMatch || descMatch) {
                      opportunities.push({
                        title: titleMatch ? titleMatch[1].trim() : "Opportunity",
                        description: descMatch ? descMatch[1].trim() : itemContent,
                        impact: impactMatch ? impactMatch[1].trim() : "",
                        complexity: complexityMatch ? complexityMatch[1].trim() : ""
                      });
                    }
                  }
                  
                  // If no structured items were found, use the entire content
                  if (opportunities.length === 0 && rawContent.trim()) {
                    return (
                      <div key={index} className="mb-10 last:mb-0">
                        <div className="flex items-center mb-4">
                          <div className="p-2 rounded-lg bg-[#FF7066]/10 mr-3">
                            <insight.icon className="w-6 h-6 text-[#FF7066]" />
                          </div>
                          <h3 className="text-xl font-bold text-primary">{insight.title}</h3>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg border border-gray-200">
                          <pre className="whitespace-pre-wrap font-sans text-secondary">
                            {rawContent}
                          </pre>
                        </div>
                      </div>
                    );
                  }
                  
                  // Return structured items in a professional report format
                  return (
                    <div key={index} className="mb-10 last:mb-0">
                      <div className="flex items-center mb-4">
                        <div className="p-2 rounded-lg bg-[#FF7066]/10 mr-3">
                          <insight.icon className="w-6 h-6 text-[#FF7066]" />
                        </div>
                        <h3 className="text-xl font-bold text-primary">{insight.title}</h3>
                      </div>
                      
                      <div className="space-y-6">
                        {opportunities.map((item, itemIdx) => (
                          <div key={itemIdx} className="bg-white p-6 rounded-lg border border-gray-200">
                            <div className="flex items-center mb-3">
                              <div className="flex-shrink-0 p-1 bg-[#FF7066]/10 rounded-lg mr-3">
                                <span className="block w-5 h-5 text-[#FF7066] font-bold text-center">
                                  {itemIdx + 1}
                                </span>
                              </div>
                              <h4 className="text-lg font-bold text-primary">{item.title}</h4>
                            </div>
                            
                            <div className="ml-9 space-y-3">
                              <div>
                                <p className="text-secondary">{item.description}</p>
                              </div>
                              
                              {item.impact && (
                                <div className="bg-gray-50 p-3 rounded border border-gray-100">
                                  <p className="text-sm">
                                    <span className="font-medium">Business Impact:</span> {item.impact}
                                  </p>
                                </div>
                              )}
                              
                              {item.complexity && (
                                <div className="flex items-center">
                                  <span className="text-sm font-medium text-gray-700 mr-2">
                                    Implementation:
                                  </span>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    item.complexity.toLowerCase().includes('low') ? 'bg-green-100 text-green-800' : 
                                    item.complexity.toLowerCase().includes('medium') ? 'bg-yellow-100 text-yellow-800' : 
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {item.complexity}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                <div className="mt-8 p-4 bg-[#FF7066]/5 rounded-lg border border-[#FF7066]/20">
                  <p className="text-sm text-primary">
                    <strong>Note:</strong> Your comprehensive report will include additional context, detailed implementation steps, ROI calculations, and industry-specific recommendations from our expert consultants.
                  </p>
                </div>
              </motion.div>
            )}
            
            {/* Next Steps Section - Keeping existing functionality */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-8 border border-gray-100"
            >
              <div className="flex items-start mb-6">
                <div className="p-3 bg-[#FF7066]/10 rounded-lg mr-4">
                  <CalendarIcon className="w-8 h-8 text-[#FF7066]" />
                </div>
                
                <div>
                  <h2 className="text-2xl font-bold text-primary mb-2">Next Steps</h2>
                  <p className="text-secondary mb-4">
                    Schedule a complimentary 30-minute consultation with one of our AI experts to discuss your report findings and implementation strategies.
                  </p>
                  
                  <a
                    href="https://calendly.com/amrit-betterbyai/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors"
                  >
                    Schedule Your Free Consultation
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-center mt-12"
            >
              <Link
                to="/"
                className="text-[#FF7066] font-medium hover:underline"
              >
                Return to Homepage
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}